import { Drawer, useDrawerProgress } from "@-/com.npmjs.react-native-drawer-layout";
import { State, type LegacyPanGesture } from "@-/com.npmjs.react-native-gesture-handler";
import {
  useAnimatedReaction,
  useSharedValue,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { useTheme } from "@-/com.npmjs.expo-router/react-navigation";
import { Platform, useWindowDimensions, View } from "@-/com.npmjs.react-native";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useCSSVariable } from "@-/com.npmjs.uniwind";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useLocalAccount } from "@-/dev.improve.haniverse.lib.auth.client";
import { useCallback, useMemo, useState, type ComponentProps, type ReactNode } from "react";
import { NavAccountDrawerContent } from "#nav-account-drawer-content";
import {
  NavAccountDrawerCloseContext,
  NavAccountDrawerContext,
  NavAccountDrawerNotificationsContext,
  NavAccountDrawerVisibilityContext,
} from "#nav-account-drawer-context";
import { useNavAccountDrawerNotifications } from "#use-nav-account-drawer-notifications";
import {
  DRAWER_OPENING_HORIZONTAL_DISTANCE,
  DRAWER_OPENING_VERTICAL_TOLERANCE,
  getDrawerOpeningTouchIntent,
} from "#drawer-opening-touch-intent";

/** Panel content width cap; wide windows (landscape phones, tablets) keep a sidebar-sized panel. */
const maxDrawerContentWidth = 400;

const drawerOverlayStyle = {
  backgroundColor: "rgba(0, 0, 0, 0.32)",
} as const;

// Drawer 4.x constructs Gesture.Pan(), but its declaration imports PanGesture,
// which Gesture Handler 3 now uses for the hooks API. Check the actual API before
// narrowing; an upstream migration must be handled explicitly.
function isLegacyDrawerPanGesture(gesture: unknown): gesture is LegacyPanGesture {
  return (
    gesture !== null &&
    typeof gesture === "object" &&
    [
      "activeOffsetX",
      "failOffsetY",
      "manualActivation",
      "maxPointers",
      "onTouchesDown",
      "onTouchesMove",
    ].every((method) => method in gesture && typeof Reflect.get(gesture, method) === "function")
  );
}

/** Share the drawer's actual animation with controls rendered above its scrim. */
function DrawerProgressBridge({ drawerProgress }: { drawerProgress: SharedValue<number> }) {
  const progress = useDrawerProgress();
  useAnimatedReaction(
    () => progress.get(),
    (value) => {
      drawerProgress.set(value);
    },
    [drawerProgress, progress],
  );
  return null;
}

/** Theme colors from `useCSSVariable` may be HSL channels or resolved hex/rgb/hsl strings. */
function hslFromChannels(channels: string | number | undefined): string | undefined {
  if (channels === undefined || channels === "") {
    return undefined;
  }

  const value = String(channels);
  if (value.startsWith("hsl") || value.startsWith("#") || value.startsWith("rgb")) {
    return value;
  }

  return `hsl(${value})`;
}

/**
 * Account drawer that slides in beside the native app shell.
 *
 * WHY THIS IS NOT A NAVIGATOR. It used to be a `createDrawerNavigator` wrapping the shell as its
 * single `"shell"` screen, on the reasoning that "this drawer is an overlay, not a navigation
 * destination, so it leaves the route tree untouched". That reasoning was wrong, and it broke every
 * navigation into an `(app)` child — including the cold-start redirect, which is what left the app
 * showing nav chrome over a permanently empty body.
 *
 * A `createDrawerNavigator` ALWAYS injects a real navigator node (with a real screen) into React
 * Navigation's runtime state, even when expo-router's linking config knows nothing about it. So the
 * live tree became `(nav) → (app) → [drawer: "shell"] → [tabs: …]` while the linking config still
 * described `(nav) → (app) → learn → summary`. expo-router resolves a navigation by walking the
 * desired state against the live state (`findDivergentState`) and dispatching at the first node
 * where they diverge. At `(app)` it compared `"learn"` against `"shell"`, diverged immediately, and
 * dispatched at the DRAWER — which has no `"learn"` route (and, for a `replace`, has the action
 * rewritten to `JUMP_TO`), so the action was silently dropped. Nothing reached the tabs navigator;
 * with `lazy: true` no scene ever mounted, while the tab bar still painted from `state.routes`.
 * `"shell"` is invisible to the linking config, so this severed EVERY route into `(app)`.
 *
 * `react-native-drawer-layout` is the standalone primitive that `@react-navigation/drawer` is
 * itself built on: same panel, same slide animation, same scrim and gesture handling — with no
 * navigator and no screen, so the route tree really is untouched this time. Open state lives in
 * this component and is published through `NavAccountDrawerContext` for the trigger.
 *
 * The drawer lives in `NavLayout`, ABOVE the stack containing both `(app)` and `auth`. This moves
 * the whole destination — header and tab bar included — as one surface, and keeps the closing
 * slide alive when sign-in replaces the app beneath the scrim. It adds no navigator node.
 *
 * `drawerStyle.backgroundColor` is what keeps the panel from rendering see-through: the original
 * hand-rolled `Modal` had no opaque surface of its own, so the scene showed through its contents.
 *
 * No account, no drawer: the panel is the account's, so it neither renders nor opens (by trigger,
 * edge swipe or Android edge-back) on a device without a local account, i.e. on the welcome gate.
 * Inside the native app there always is one (`AppLocalAccountGate`).
 */
export function NavAccountDrawer({
  children,
  onOpenChange,
  trigger,
}: {
  children: ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  trigger?: ReactNode;
}) {
  const { colors } = useTheme();
  const [isEdgeNavigationEnabled] = useDeviceSetting("isEdgeNavigationEnabled");
  const windowDimensions = useWindowDimensions();
  const { width: windowWidth } = windowDimensions;
  const { right: rightInset } = useSafeAreaInsets();
  const { diameter } = useToolbarGridMetrics();
  const backgroundChannels = useCSSVariable("--color-background");
  const borderChannels = useCSSVariable("--color-border");
  const drawerBackgroundColor = hslFromChannels(backgroundChannels) ?? colors.background;
  const drawerBorderColor = hslFromChannels(borderChannels) ?? colors.border;

  const account = useLocalAccount();
  const hasAccount = account !== null;
  const [isOpen, setIsOpen] = useState(false);
  const drawerProgress = useSharedValue(0);
  const openingTouch = useSharedValue({ startedAt: 0, x: 0, y: 0 });
  const configureGestureHandler = useCallback<
    NonNullable<ComponentProps<typeof Drawer>["configureGestureHandler"]>
  >(
    (gesture) => {
      if (isOpen || Platform.OS !== "ios") {
        return gesture;
      }
      if (!isLegacyDrawerPanGesture(gesture)) {
        throw new Error(
          "The account drawer's opening gesture requires the legacy Pan gesture API.",
        );
      }
      gesture
        .manualActivation(true)
        .maxPointers(1)
        .activeOffsetX(-DRAWER_OPENING_HORIZONTAL_DISTANCE)
        .failOffsetY([-DRAWER_OPENING_VERTICAL_TOLERANCE, DRAWER_OPENING_VERTICAL_TOLERANCE])
        .onTouchesDown((event, manager) => {
          "worklet";
          if (event.state !== State.UNDETERMINED && event.state !== State.BEGAN) {
            return;
          }
          const touch = event.allTouches[0];
          if (touch === undefined || event.numberOfTouches !== 1) {
            manager.fail();
            return;
          }
          openingTouch.set({ startedAt: Date.now(), x: touch.absoluteX, y: touch.absoluteY });
        })
        .onTouchesMove((event, manager) => {
          "worklet";
          // Once claimed, let the drawer keep tracking through pauses and vertical drift.
          // Once yielded, never reclaim the same touch even if its direction reverses.
          // Do not replace its onStart/onUpdate/onEnd/onFinalize animation callbacks.
          if (event.state !== State.UNDETERMINED && event.state !== State.BEGAN) {
            return;
          }
          const touch = event.allTouches[0];
          if (touch === undefined || event.numberOfTouches !== 1) {
            manager.fail();
            return;
          }
          const start = openingTouch.get();
          const intent = getDrawerOpeningTouchIntent({
            distanceX: touch.absoluteX - start.x,
            distanceY: touch.absoluteY - start.y,
            elapsedMs: Date.now() - start.startedAt,
          });
          if (intent === "scroll") {
            manager.fail();
          } else if (intent === "drawer") {
            manager.activate();
          }
        });
      return gesture;
    },
    [isOpen, openingTouch],
  );
  // Narrow windows leave only the close-button gutter exposed; wider windows cap the panel so
  // it does not stretch across the whole screen. The right inset sits inside the panel.
  const drawerWidth = Math.max(
    0,
    Math.min(windowWidth - diameter - 18, maxDrawerContentWidth + rightInset),
  );
  const { hasNotifications, notifications } = useNavAccountDrawerNotifications();
  const openDrawer = useCallback(() => {
    if (!hasAccount) {
      return;
    }
    setIsOpen(true);
    onOpenChange?.(true);
  }, [hasAccount, onOpenChange]);
  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const visibility = useMemo(() => ({ isOpen }), [isOpen]);
  const contextValue = useMemo(
    () => ({
      closeDrawer,
      drawerProgress,
      drawerWidth,
      hasNotifications,
      isOpen,
      notifications,
      openDrawer,
    }),
    [closeDrawer, drawerProgress, drawerWidth, hasNotifications, isOpen, notifications, openDrawer],
  );

  const drawerStyle = useMemo(
    () => ({
      backgroundColor: drawerBackgroundColor,
      borderLeftColor: drawerBorderColor,
      borderLeftWidth: 1,
      // Leave the translated close button between matching 9px gutters.
      width: drawerWidth,
    }),
    [drawerBackgroundColor, drawerBorderColor, drawerWidth],
  );

  // Keep the JSX in this component's compiler cache. The upstream drawer calls its render
  // callback on every open-state commit; returning the existing node lets React skip the rows.
  const drawerContent = account === null ? null : <NavAccountDrawerContent account={account} />;
  const renderDrawerContent = () => drawerContent;

  return (
    <NavAccountDrawerCloseContext value={closeDrawer}>
      <NavAccountDrawerNotificationsContext value={notifications}>
        <NavAccountDrawerContext value={contextValue}>
          <NavAccountDrawerVisibilityContext value={visibility}>
            <View className="flex-1">
              <Drawer
                configureGestureHandler={configureGestureHandler}
                drawerPosition="right"
                drawerStyle={drawerStyle}
                // "slide" moves the drawer and the whole app shell together. The overlay belongs to the
                // shifted shell, creating the soft shadow over the exposed edge instead of covering it
                // with the drawer.
                drawerType="slide"
                // Panel width and animation geometry use the same synchronous rotation snapshot.
                layout={windowDimensions}
                onClose={closeDrawer}
                onOpen={openDrawer}
                open={isOpen}
                overlayStyle={drawerOverlayStyle}
                renderDrawerContent={renderDrawerContent}
                // The drawer owns continuous dragging and release/cancellation. Restrict opening
                // to the edge so horizontal gestures inside cards and pagers stay independent.
                swipeEdgeWidth={24}
                swipeEnabled={isEdgeNavigationEnabled && hasAccount}
              >
                <DrawerProgressBridge drawerProgress={drawerProgress} />
                {children}
                {/* The ancestor pan detector observes edge gestures without covering controls.
                    An edge hit surface swallows Android taps on the card's Details tab. */}
              </Drawer>
              {trigger}
            </View>
          </NavAccountDrawerVisibilityContext>
        </NavAccountDrawerContext>
      </NavAccountDrawerNotificationsContext>
    </NavAccountDrawerCloseContext>
  );
}
